import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Star, Trash2, Flag, CheckCircle2 } from 'lucide-react';
import api from '@/lib/api';

function Stars({ rating }) {
  return (
    <div className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= rating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-slate-300'}`} />
      ))}
    </div>
  );
}

export default function AdminModerationPage() {
  const [reviews, setReviews] = useState([]);
  const [eventReports, setEventReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);
    try {
      const [r, er] = await Promise.all([
        api.get('/admin/reviews'),
        api.get('/admin/event-reports'),
      ]);
      setReviews(r.data.reviews || []);
      setEventReports(er.data.reports || []);
    } catch {
      toast.error('Failed to load moderation data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const deleteReview = async (id) => {
    if (!window.confirm('Permanently delete this review?')) return;
    try {
      await api.delete(`/admin/reviews/${id}`);
      setReviews(prev => prev.filter(r => r.review_id !== id));
      toast.success('Review deleted');
    } catch { toast.error('Failed to delete review'); }
  };

  const resolveReport = async (id, status) => {
    try {
      await api.post(`/admin/event-reports/${id}/resolve`, { status });
      setEventReports(prev => prev.map(r => r.report_id === id ? { ...r, status } : r));
      toast.success(`Report marked ${status}`);
    } catch { toast.error('Failed to update report'); }
  };

  return (
    <div data-testid="admin-moderation-page" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Dashboard
      </Link>
      <h1 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Reviews &amp; Event Reports</h1>

      {loading && <p className="text-slate-400">Loading…</p>}

      {/* Event Reports */}
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Flag className="w-5 h-5 text-[#df5681]" /> Event Reports ({eventReports.length})
      </h2>
      <div className="space-y-3 mb-10">
        {eventReports.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No event reports.</p>
        ) : (
          eventReports.map(r => (
            <Card key={r.report_id} className="border-slate-100" data-testid={`event-report-${r.report_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{r.event_title}</p>
                    <p className="text-xs text-slate-500">
                      Reported by {r.reporter_email} · {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${r.status === 'pending' ? 'bg-amber-100 text-amber-700' : r.status === 'resolved' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {r.status}
                  </span>
                </div>
                <p className="text-sm mb-1"><strong>Reason:</strong> {r.reason}</p>
                {r.details && <p className="text-sm text-slate-600 mb-2 whitespace-pre-wrap">&ldquo;{r.details}&rdquo;</p>}
                <div className="flex gap-2 mt-2">
                  <Link to={`/events/${r.event_id}`}>
                    <Button size="sm" variant="outline" className="rounded-full text-xs">View Event</Button>
                  </Link>
                  {r.status === 'pending' && (
                    <>
                      <Button size="sm" onClick={() => resolveReport(r.report_id, 'resolved')} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] text-xs gap-1" data-testid={`resolve-report-${r.report_id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> Mark Resolved
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => resolveReport(r.report_id, 'dismissed')} className="rounded-full text-xs text-slate-500">
                        Dismiss
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Reviews */}
      <h2 className="text-lg font-semibold mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
        <Star className="w-5 h-5 text-[#f59e0b]" /> Player Reviews ({reviews.length})
      </h2>
      <div className="space-y-3">
        {reviews.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No reviews yet.</p>
        ) : (
          reviews.map(r => (
            <Card key={r.review_id} className="border-slate-100" data-testid={`admin-review-${r.review_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <p className="text-sm">
                      <strong>{r.reviewer_name}</strong> reviewed <strong>{r.reviewed_user_name}</strong>
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Stars rating={r.rating} />
                      <span className="text-xs text-slate-400">{new Date(r.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => deleteReview(r.review_id)} className="text-slate-400 hover:text-red-500 gap-1 text-xs" data-testid={`delete-review-${r.review_id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </Button>
                </div>
                {r.comment && <p className="text-sm text-slate-600 whitespace-pre-wrap">&ldquo;{r.comment}&rdquo;</p>}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
